import styled, { keyframes } from 'styled-components';
import { NavLink } from 'react-router-dom';

// ------------------------------------------------------------------ //
// Global shell: top nav, mobile bottom nav + sheets, footer, grain.
// ------------------------------------------------------------------ //

// The JS desktop/mobile split flips at 1080px (useWindowListener touchDevice),
// so the shell's CSS breakpoint matches it exactly.
export const SHELL_BREAK = '@media (max-width: 1079px)';

/* Floating "island" bar (otok × zavjesa): a detached capsule spanning the
   content width. The »novi kviz« menu is not a dropdown - it opens INSIDE
   the capsule (.stage), which grows slightly past the content edges and
   follows the app theme. */
export const TopNavBar = styled.nav<{
	$scrolled?: boolean;
	$hidden?: boolean;
	$bare?: boolean;
	$open?: boolean;
}>`
	position: fixed;
	top: ${({ $bare, $scrolled }) => ($bare ? '0' : $scrolled ? '8px' : '14px')};
	left: ${({ $bare, $open }) =>
		$bare ? '0' : $open ? 'max(10px, calc(var(--pad) - 18px))' : 'var(--pad)'};
	right: ${({ $bare, $open }) =>
		$bare ? '0' : $open ? 'max(10px, calc(var(--pad) - 18px))' : 'var(--pad)'};
	z-index: 1970;
	overflow: hidden;
	text-align: left;
	/* closed bar is ~58px tall, so 29px IS the pill shape - an explicit value
	   keeps the radius nearly constant while the stage animates open (a 999px
	   pill radius would lens-crop the growing content) */
	border-radius: ${({ $bare, $open }) =>
		$bare ? '0' : $open ? '26px' : '29px'};
	background: ${({ theme }) =>
		theme.mode === 'dark' ? 'rgba(38,40,43,0.9)' : 'rgba(255,249,238,0.92)'};
	backdrop-filter: blur(18px) saturate(1.6);
	-webkit-backdrop-filter: blur(18px) saturate(1.6);
	border: 1px solid
		${({ theme }) =>
			theme.mode === 'dark'
				? 'rgba(247,247,247,0.12)'
				: 'rgba(var(--ink-rgb), 0.1)'};
	box-shadow: ${({ theme, $open }) =>
		theme.mode === 'dark'
			? $open
				? '0 40px 90px rgba(0,0,0,0.55)'
				: '0 10px 30px rgba(0,0,0,0.35)'
			: $open
				? '0 34px 80px rgba(24,14,2,0.22)'
				: '0 10px 30px rgba(24,14,2,0.08)'};
	transition:
		top 0.3s,
		left 0.45s cubic-bezier(0.22, 0.9, 0.3, 1),
		right 0.45s cubic-bezier(0.22, 0.9, 0.3, 1),
		border-radius 0.35s,
		box-shadow 0.35s,
		transform 0.35s,
		opacity 0.3s;

	/* scroll down -> bar slides away; any scroll up brings it back. Kept
	   visible while the stage is open so the menu can't fly off mid-use. */
	${({ $hidden, $open }) =>
		$hidden && !$open
			? `transform: translateY(calc(-100% - 16px));
	opacity: 0;
	pointer-events: none;`
			: ''}

	.top {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 8px 12px 8px 22px;
	}

	/* quiz play: plain full-width bar aligned to the quiz content column */
	${({ $bare, $scrolled }) =>
		$bare
			? `background: none;
	backdrop-filter: none;
	-webkit-backdrop-filter: none;
	border: none;
	box-shadow: none;
	overflow: visible;

	.top {
		max-width: 1900px;
		margin: 0 auto;
		width: 100%;
		/* horizontal padding tracks the FramedCover inset (14px mobile,
		   24px desktop) so the bar sits on the cover photo's edges */
		padding: ${$scrolled ? '12px' : '18px'} 14px;
		transition: padding 0.35s;
	}
	@media (min-width: 1080px) {
		.top {
			padding: ${$scrolled ? '12px' : '18px'} 24px;
		}
	}
	/* phone play bar carries logo + collab lockup + three controls on
	   ~375px — everything tightens a notch so the row can't overflow.
	   .top prefix outranks the base rules declared later in this template */
	@media (max-width: 1079px) {
		.top .links {
			gap: 10px;
		}
		.top .nav-collab {
			margin-left: 10px;
			gap: 8px;
		}
		.top .nav-logo-nogac {
			height: 28px;
		}
		.top .nav-logo-vragolasti {
			height: 24px;
		}
	}`
			: ''}

	.links {
		display: flex;
		align-items: center;
		gap: 16px;
	}

	.nav-collab {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-left: 14px;
	}

	.nav-logo-nogac {
		height: 34px;
		width: auto;
		display: block;
	}

	.nav-logo-vragolasti {
		height: 30px;
		width: auto;
		display: block;
	}

	/* ------- centered action group: teal CTA + quiet collection link ------- */
	/* absolutely centered in the top row so it never shifts with the section
	   label on the left or login/avatar width on the right */
	.center {
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
		display: flex;
		align-items: center;
		gap: 26px;
	}

	.cta {
		display: inline-flex;
		align-items: center;
		gap: 10px;
		font-family: var(--fd);
		font-size: 14px;
		letter-spacing: 0.4px;
		text-transform: uppercase;
		padding: 10px 20px;
		border-radius: var(--r-pill);
		border: none;
		cursor: pointer;
		background: var(--teal);
		color: var(--teal-deep);
		transition:
			transform 0.18s,
			box-shadow 0.25s,
			background 0.2s;
	}

	.cta:hover {
		background: var(--teal-soft);
		transform: translateY(-2px);
		box-shadow: 0 14px 36px rgba(15, 62, 50, 0.22);
	}

	.cta:active {
		transform: scale(0.97);
	}

	/* ------- the stage: menu content inside the capsule ------- */
	.stage {
		max-height: ${({ $open }) => ($open ? '640px' : '0')};
		opacity: ${({ $open }) => ($open ? '1' : '0')};
		overflow: hidden;
		transition:
			max-height 0.5s cubic-bezier(0.22, 0.9, 0.3, 1),
			opacity 0.3s;
	}

	.rows {
		padding: 2px clamp(16px, 2.2vw, 34px) 0;
	}

	.irow {
		width: 100%;
		display: flex;
		align-items: center;
		gap: clamp(14px, 2.2vw, 28px);
		padding: clamp(13px, 1.6vw, 19px) 4px;
		background: none;
		border: none;
		border-top: 1px dashed rgba(var(--ink-rgb), 0.14);
		cursor: pointer;
		text-align: left;
		opacity: ${({ $open }) => ($open ? '1' : '0')};
		transform: ${({ $open }) => ($open ? 'none' : 'translateY(14px)')};
		transition:
			opacity 0.3s,
			transform 0.35s;
	}

	.irow:first-child {
		border-top: none;
	}

	${({ $open }) =>
		$open
			? `.irow:nth-child(1) { transition-delay: 0.1s; }
	.irow:nth-child(2) { transition-delay: 0.17s; }
	.irow:nth-child(3) { transition-delay: 0.24s; }`
			: ''}

	.irow .num {
		font-family: var(--fh);
		font-weight: 600;
		font-size: clamp(17px, 1.6vw, 22px);
		color: var(--gold-ink);
		width: 36px;
		flex-shrink: 0;
	}

	.irow b {
		font-family: var(--fd);
		font-weight: 400;
		font-size: clamp(23px, 2.9vw, 38px);
		letter-spacing: 0.4px;
		text-transform: uppercase;
		color: var(--ink);
		display: block;
		line-height: 1.05;
		transition: color 0.2s;
	}

	.irow:hover b {
		color: var(--teal-dark);
	}

	.irow u {
		text-decoration: none;
		display: block;
		font-family: var(--fb);
		font-size: 13px;
		color: var(--mut);
		font-weight: 500;
		margin-top: 3px;
		line-height: 1.4;
	}

	.irow .arr {
		margin-left: auto;
		width: 40px;
		height: 40px;
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: var(--ink);
		flex-shrink: 0;
		transition:
			background 0.2s,
			border-color 0.2s,
			color 0.2s;
	}

	.irow:hover .arr {
		background: var(--teal);
		border-color: var(--teal);
		color: var(--teal-deep);
	}

	/* ------- notifications inside the stage (bell opens the island) ------- */
	.nstage {
		padding: 6px clamp(16px, 2.2vw, 34px) 22px;
	}

	/* handwritten gold cap, matching the mobile C4 gold-note card */
	.ncap {
		display: inline-block;
		font-family: var(--fh);
		font-weight: 600;
		font-size: 19px;
		letter-spacing: 0;
		text-transform: lowercase;
		/* gold only sits on dark surfaces; flips to gold-ink on cream */
		color: ${({ theme }) =>
			theme.mode === 'dark' ? 'var(--gold)' : 'var(--gold-ink)'};
		transform: rotate(-1.2deg);
		padding: 6px 4px 2px;
	}

	.nr {
		position: relative;
		display: block;
		width: 100%;
		text-align: left;
		background: none;
		border: 0;
		border-top: 1px dashed rgba(var(--ink-rgb), 0.14);
		padding: 14px 4px 14px 24px;
		opacity: ${({ $open }) => ($open ? '1' : '0')};
		transform: ${({ $open }) => ($open ? 'none' : 'translateY(14px)')};
		transition:
			opacity 0.3s,
			transform 0.35s;
	}

	/* gold bullet in front of each notification */
	.nr::before {
		content: '';
		position: absolute;
		left: 5px;
		top: 22px;
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: ${({ theme }) =>
			theme.mode === 'dark' ? 'var(--gold)' : 'var(--gold-ink)'};
	}

	/* rows with a destination render as buttons */
	button.nr {
		cursor: pointer;
	}

	button.nr:hover b {
		color: var(--teal-dark);
	}

	.nempty {
		margin: 0;
		padding: 12px 4px 10px;
		font-family: var(--fb);
		font-size: 14.5px;
		font-weight: 500;
		color: var(--mut);
	}

	.nr:first-of-type {
		border-top: none;
	}

	${({ $open }) =>
		$open
			? `.nr:nth-of-type(1) { transition-delay: 0.1s; }
	.nr:nth-of-type(2) { transition-delay: 0.17s; }
	.nr:nth-of-type(3) { transition-delay: 0.24s; }
	.nr:nth-of-type(4) { transition-delay: 0.31s; }`
			: ''}

	.nr b {
		display: block;
		font-family: var(--fb);
		font-size: 15.5px;
		font-weight: 700;
		color: var(--ink);
		line-height: 1.35;
	}

	.nr u {
		text-decoration: none;
		display: block;
		font-family: var(--fb);
		font-size: 13.5px;
		font-weight: 500;
		color: var(--mut);
		margin-top: 3px;
		line-height: 1.45;
	}

	/* quiet non-product strip at the bottom of the stage */
	.sfoot {
		display: flex;
		align-items: center;
		gap: 4px;
		flex-wrap: wrap;
		margin-top: 8px;
		padding: 10px clamp(12px, 1.8vw, 28px);
		border-top: 1px solid rgba(var(--ink-rgb), 0.1);
		opacity: ${({ $open }) => ($open ? '1' : '0')};
		transition: opacity 0.3s ${({ $open }) => ($open ? '0.25s' : '0s')};
	}

	.srow {
		display: inline-flex;
		align-items: center;
		gap: 9px;
		padding: 8px 12px;
		font-family: var(--fb);
		font-size: 13.5px;
		font-weight: 700;
		color: var(--mut);
		background: none;
		border: none;
		border-radius: var(--r-ctl);
		cursor: pointer;
		transition:
			background 0.15s,
			color 0.15s;
	}

	.srow:hover {
		background: rgba(var(--ink-rgb), 0.05);
		color: var(--teal-dark);
	}

	${SHELL_BREAK} {
		top: 0;
		left: 0;
		right: 0;

		/* on touch widths the shell bar drops the floating-pill act: it's a
		   plain transparent strip at the very top of the page that simply
		   scrolls away with the content (no scroll-return either — the
		   transform/opacity overrides beat the base $hidden rule). The bare
		   play bar keeps its fixed overlay behavior. */
		${({ $bare }) =>
			$bare
				? ''
				: `position: absolute;
		top: 14px;
		border-radius: 0;
		border: none;
		background: none;
		backdrop-filter: none;
		-webkit-backdrop-filter: none;
		box-shadow: none;
		transform: none;
		opacity: 1;
		pointer-events: auto;`}

		.center,
		.stage {
			display: none;
		}
	}
`;

/* Dim layer behind the open stage; sits under the bar, over the page. */
export const NavBackdrop = styled.div<{ $open?: boolean }>`
	position: fixed;
	inset: 0;
	z-index: 1969;
	background: ${({ theme }) =>
		theme.mode === 'dark' ? 'rgba(10,11,12,0.55)' : 'rgba(27,20,14,0.26)'};
	opacity: ${({ $open }) => ($open ? 1 : 0)};
	pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
	transition: opacity 0.35s;

	${SHELL_BREAK} {
		display: none;
	}
`;

/* Small uppercase wayfinding label next to the logo inside creation flows. */
export const NavSectionLabel = styled.span`
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 1.6px;
	text-transform: uppercase;
	color: var(--mut);
	padding-left: 16px;
	margin-left: 16px;
	border-left: 1px solid rgba(var(--ink-rgb), 0.2);
	white-space: nowrap;

	${SHELL_BREAK} {
		display: none;
	}
`;

/* Outlined pill: profile ("moji kvizovi" with avatar) or "prijavi se".
   Fixed 42px height to match NavCartLink's circle. */
export const NavGhostLink = styled(NavLink)`
	display: inline-flex;
	align-items: center;
	gap: 10px;
	height: 42px;
	font-family: var(--fd);
	font-size: 14px;
	line-height: 1;
	letter-spacing: 0.4px;
	text-transform: uppercase;
	padding: 0 18px;
	border-radius: var(--r-pill);
	border: 1.5px solid rgba(var(--ink-rgb), 0.3);
	color: var(--brown);
	text-decoration: none;
	background: transparent;
	transition:
		border-color 0.2s,
		color 0.2s,
		transform 0.18s;

	&:hover {
		border-color: var(--ink);
		color: var(--ink);
		transform: translateY(-2px);
	}

	&.active {
		border-color: var(--teal-dark);
		color: var(--teal-dark);
	}

	${SHELL_BREAK} {
		display: none;
	}
`;

/* Logged-in profile link, »prelijevanje« blended into the island itself:
   icon-only since the »moji kvizovi« label moved into the centered collection
   link - the avatar photo bleeds full-height to the capsule's right edge,
   mask-fading into the bar. Negative margins escape .top's padding
   (8px 12px 8px 22px) so the photo is clipped by the island's own radius
   (TopNavBar has overflow: hidden). NavLink auto-appends .active. */
export const NavAvatarLink = styled(NavLink)`
	position: relative;
	display: inline-flex;
	align-items: center;
	width: 84px;
	height: 58px;
	margin: -8px -12px -8px 0;
	flex-shrink: 0;
	text-decoration: none;

	img {
		position: absolute;
		top: 0;
		right: 0;
		width: 84px;
		height: 100%;
		object-fit: cover;
		/* avatars are full-body figures - crop toward the head */
		object-position: center 15%;
		display: block;
		-webkit-mask-image: linear-gradient(90deg, transparent 0, #000 55%);
		mask-image: linear-gradient(90deg, transparent 0, #000 55%);
		transition: transform 0.25s;
	}

	/* fallback when the profile has no avatar picture yet */
	.fb {
		position: absolute;
		right: 34px;
		top: 50%;
		transform: translateY(-50%);
		display: inline-flex;
		color: var(--mut);
	}

	&:hover img {
		transform: scale(1.05);
	}

	${SHELL_BREAK} {
		display: none;
	}
`;

/* Quiet uppercase link beside the CTA (»tihi link«): no outline, just the
   display face led by the collection glyph (same IcCards as the mobile
   bottom-nav tab); the whole link lights teal on hover and on the active
   route via currentColor. */
export const NavCollectionLink = styled(NavLink)`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-family: var(--fd);
	font-size: 13px;
	letter-spacing: 0.5px;
	text-transform: uppercase;
	color: var(--brown);
	text-decoration: none;
	transition: color 0.2s;

	&:hover,
	&.active {
		color: var(--teal-dark);
	}
`;

/* Bordered bell circle: opens the notification stage inside the island.
   Same grammar as the cart circle; the coral badge counts unseen broadcast
   notifications and clears once the stage is opened. */
export const NavBellButton = styled.button<{ $open?: boolean }>`
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 42px;
	height: 42px;
	border-radius: 50%;
	border: 1.5px solid
		${({ $open }) => ($open ? 'var(--teal-dark)' : 'rgba(var(--ink-rgb), 0.3)')};
	color: ${({ $open }) => ($open ? 'var(--teal-dark)' : 'var(--brown)')};
	background: none;
	cursor: pointer;
	flex-shrink: 0;
	transition:
		border-color 0.2s,
		color 0.2s;

	&:hover {
		border-color: var(--teal-dark);
		color: var(--teal-dark);
	}

	.badge {
		position: absolute;
		top: -5px;
		right: -5px;
		background: var(--coral);
		color: #fff;
		font-size: 9.5px;
		font-weight: 700;
		width: 16px;
		height: 16px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 2px solid
			${({ theme }) =>
				theme.mode === 'dark' ? 'var(--black)' : 'var(--cream)'};
	}

	${SHELL_BREAK} {
		display: none;
	}
`;

/* Bordered cart circle with the item-count badge. */
export const NavCartLink = styled(NavLink)`
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 42px;
	height: 42px;
	border-radius: 50%;
	border: 1.5px solid rgba(var(--ink-rgb), 0.3);
	color: var(--brown);
	text-decoration: none;
	transition:
		border-color 0.2s,
		color 0.2s;

	&:hover,
	&.active {
		border-color: var(--teal-dark);
		color: var(--teal-dark);
	}

	.badge {
		position: absolute;
		top: -5px;
		right: -5px;
		background: var(--coral);
		color: #fff;
		font-size: 9.5px;
		font-weight: 700;
		width: 16px;
		height: 16px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 2px solid
			${({ theme }) =>
				theme.mode === 'dark' ? 'var(--black)' : 'var(--cream)'};
	}

	${SHELL_BREAK} {
		display: none;
	}
`;

/* Rendered only in quiz play. Follows the app theme; the solid page-tone
   disc keeps it readable when it sits over a dark hero photo. */
export const NavCloseLink = styled(NavLink)`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	flex-shrink: 0;

	@media (min-width: 1080px) {
		width: 42px;
		height: 42px;
	}
	border-radius: 50%;
	border: 1.5px solid rgba(var(--ink-rgb), 0.3);
	background: var(--cream);
	color: var(--ink);
	text-decoration: none;
	transition: border-color 0.15s;
	-webkit-tap-highlight-color: transparent;

	&:hover {
		border-color: var(--ink);
	}
`;

/* Fullscreen toggle on the play bar — same circle language as the close
   button, sits right of the theme picker. */
export const NavExpandButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	flex-shrink: 0;

	@media (min-width: 1080px) {
		width: 42px;
		height: 42px;
	}
	border-radius: 50%;
	border: 1.5px solid rgba(var(--ink-rgb), 0.3);
	background: var(--cream);
	color: var(--ink);
	cursor: pointer;
	transition: border-color 0.15s;
	-webkit-tap-highlight-color: transparent;

	&:hover {
		border-color: var(--ink);
	}

	svg path {
		fill: currentColor;
	}
`;

export const ThemeToggle = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 2px;
	padding: 3px;
	border-radius: var(--r-pill);
	border: 1.5px solid rgba(var(--ink-rgb), 0.22);
	background: transparent;
	cursor: pointer;
	transition: border-color 0.2s;

	&:hover {
		border-color: rgba(var(--ink-rgb), 0.45);
	}

	.opt {
		width: 30px;
		height: 30px;
		border-radius: 50%;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		color: var(--mut);
		transition: 0.2s;
	}

	.opt.on {
		background: rgba(var(--ink-rgb), 0.07);
		color: var(--teal-dark);
	}
`;

/* Language switch next to the theme toggle: quiet text trigger (HR/ENG +
   chevron), no chrome. */
export const LangSwitch = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	border: 0;
	padding: 0;
	background: none;
	cursor: pointer;
	font-family: inherit;
	font-size: 12.5px;
	font-weight: 600;
	letter-spacing: 0.06em;
	color: var(--mut);
	transition: color 0.2s;

	&:hover {
		color: var(--teal-dark);
	}

	.chev {
		display: inline-flex;
		transition: transform 0.2s;
	}

	&[aria-expanded='true'] .chev {
		transform: rotate(180deg);
	}
`;

/* The dropdown itself is portaled to <body> - the nav capsule clips its
   children (overflow: hidden), so a menu can't escape it in place. Positioned
   with an inline top/left from the trigger's rect; translateX centers it. */
export const LangMenu = styled.div`
	position: fixed;
	z-index: 1975;
	transform: translateX(-50%);
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 66px;
	padding: 5px;
	border-radius: 14px;
	background: ${({ theme }) =>
		theme.mode === 'dark' ? 'rgba(38,40,43,0.96)' : 'rgba(255,249,238,0.97)'};
	backdrop-filter: blur(18px) saturate(1.6);
	-webkit-backdrop-filter: blur(18px) saturate(1.6);
	border: 1px solid
		${({ theme }) =>
			theme.mode === 'dark'
				? 'rgba(247,247,247,0.12)'
				: 'rgba(var(--ink-rgb), 0.1)'};
	box-shadow: ${({ theme }) =>
		theme.mode === 'dark'
			? '0 10px 30px rgba(0,0,0,0.35)'
			: '0 10px 30px rgba(24,14,2,0.08)'};

	button {
		border: 0;
		background: none;
		cursor: pointer;
		padding: 7px 12px;
		border-radius: 10px;
		font-family: inherit;
		font-size: 12.5px;
		font-weight: 600;
		letter-spacing: 0.06em;
		color: var(--mut);
		transition:
			background 0.15s,
			color 0.15s;
	}

	button:hover {
		background: rgba(var(--ink-rgb), 0.07);
		color: var(--teal-dark);
	}

	button.on {
		color: var(--teal-dark);
	}
`;

export const LogoBox = styled(NavLink)`
	position: relative;
	display: block;
	width: 126px;
	height: 39px;
	flex-shrink: 0;

	svg {
		position: absolute;
		inset: 0;
		display: block;
		width: 100%;
		height: 100%;
	}

	.lg-ink {
		color: var(--logo);
	}

	.lg-teal {
		color: var(--teal);
		clip-path: inset(100% 0 0 0);
	}
`;

// ---------------------------- mobile bottom nav ---------------------------- //

export const MobileNavBar = styled.nav`
	display: none;
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 1972;
	background: ${({ theme }) =>
		theme.mode === 'dark'
			? 'linear-gradient(0deg, rgba(25,27,29,0.94), rgba(25,27,29,0.8))'
			: 'linear-gradient(0deg, rgba(248,240,227,0.92), rgba(248,240,227,0.78))'};
	backdrop-filter: blur(18px) saturate(1.6);
	-webkit-backdrop-filter: blur(18px) saturate(1.6);
	border-top: 1px solid
		${({ theme }) =>
			theme.mode === 'dark'
				? 'rgba(247,247,247,0.1)'
				: 'rgba(var(--ink-rgb), 0.1)'};
	padding: 8px 10px calc(8px + env(safe-area-inset-bottom));
	align-items: center;

	${SHELL_BREAK} {
		display: flex;
	}

	/* icon-only tabs (proposal 04): the icon itself carries the active state */
	.it {
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		text-decoration: none;
		color: var(--brown);
		background: none;
		border: none;
		cursor: pointer;
		padding: 12px 2px;
		border-radius: var(--r-ctl);
		transition: color 0.2s;
	}

	.it svg {
		transition: transform 0.2s;
	}

	.it.active {
		color: var(--teal-dark);
	}

	.it.active svg {
		transform: translateY(-1px);
	}

	/* the one loud element: »novi kviz« as a real teal pill, always on */
	.newq {
		flex: 0 0 auto;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		font-family: var(--fd);
		font-size: 13px;
		letter-spacing: 0.5px;
		text-transform: uppercase;
		color: var(--teal-deep);
		background: var(--teal);
		border: none;
		border-radius: var(--r-pill);
		padding: 12px 18px;
		margin: 0 6px;
		cursor: pointer;
	}

	.bwrap {
		position: relative;
		display: inline-flex;
	}

	/* plain unseen-notifications dot on the »više« tab (no count) */
	.ndot {
		position: absolute;
		top: -3px;
		right: -5px;
		width: 9px;
		height: 9px;
		border-radius: 50%;
		background: var(--coral);
	}

	.badge {
		position: absolute;
		top: -4px;
		right: -8px;
		background: var(--coral);
		color: #fff;
		font-size: 9.5px;
		font-weight: 700;
		width: 15px;
		height: 15px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
	}
`;

export const MobileSheet = styled.div<{ $roomyRows?: boolean }>`
	position: fixed;
	inset: 0;
	z-index: 1971;
	background: ${({ theme }) =>
		theme.mode === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(24,14,2,0.45)'};

	.in {
		position: absolute;
		left: 10px;
		right: 10px;
		bottom: calc(74px + env(safe-area-inset-bottom));
		background: ${({ theme }) =>
			theme.mode === 'dark' ? 'var(--panel)' : 'var(--cream)'};
		border-radius: var(--r-card);
		padding: 10px;
		box-shadow: ${({ theme }) =>
			theme.mode === 'dark'
				? '0 24px 60px rgba(0,0,0,0.6)'
				: '0 24px 60px rgba(24,14,2,0.35)'};
		text-align: left;
	}

	.in a,
	.in button.row {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 14px;
		text-decoration: none;
		padding: ${({ $roomyRows }) => ($roomyRows ? '32px' : '16px')} 16px;
		border-radius: 0;
		transition: background 0.15s;
		background: none;
		border: none;
		cursor: pointer;
		text-align: left;
	}

	.in a:hover,
	.in button.row:hover {
		background: rgba(var(--ink-rgb), 0.05);
	}

	.in b {
		font-family: var(--fd);
		font-weight: 400;
		font-size: 21px;
		letter-spacing: 0.3px;
		color: var(--ink);
		text-transform: uppercase;
		display: block;
	}

	.in u {
		text-decoration: none;
		display: block;
		font-size: 13.5px;
		font-family: var(--fb);
		color: var(--mut);
		font-weight: 500;
		margin-top: 2px;
	}

	.in .arr {
		width: 38px;
		height: 38px;
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: var(--ink);
		flex-shrink: 0;
	}

	/* dashed separators only in the roomy create menu - the more-sheet
	   rows (poklon kupon / info centar) sit clean, per founder */
	${({ $roomyRows }) =>
		$roomyRows
			? `.in a + a,
	.in button.row + a,
	.in a + button.row,
	.in button.row + button.row {
		border-top: 1px dashed rgba(var(--ink-rgb), 0.12);
	}`
			: ''}

	/* M10: logged-in account strip at the sheet top — avatar, handwritten
	   nickname, and the two header pills relocated as icon circles */
	.acct {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 18px 6px 18px 8px;
	}

	/* avatar + nickname act as one tap target leading to the profile */
	.acct .alink {
		display: flex;
		align-items: center;
		gap: 12px;
		flex: 1;
		min-width: 0;
		padding: 0;
		background: none;
		border: none;
		cursor: pointer;
		text-align: left;
	}

	.acct .aav {
		width: 44px;
		height: 44px;
		border-radius: 50%;
		overflow: hidden;
		flex-shrink: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		color: var(--mut);
		background: rgba(var(--ink-rgb), 0.08);
	}

	.acct .aav img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	.acct .anick {
		font-family: var(--fh);
		font-weight: 600;
		font-size: 27px;
		color: var(--coral);
		transform: rotate(-2deg);
		flex: 1;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	/* borderless icon buttons - the 40px box stays as the tap target */
	.circ {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		flex-shrink: 0;
		border: none;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		color: var(--brown);
		background: none;
		cursor: pointer;
		transition: color 0.2s;
	}

	.circ:hover {
		color: var(--ink);
	}

	.asep {
		border-top: 1px dashed rgba(var(--ink-rgb), 0.12);
		margin: 0 10px;
	}

	/* broadcast-notification mini preview at the sheet top: C4 »zlatna
	   bilješka« — gold-washed card with a handwritten cap (gold = the
	   handwriting/notes language). The card separates itself, so no
	   hairline follows the block. */
	.notifs {
		margin: 6px 6px 10px;
		padding: 12px 16px 13px;
		border-radius: 20px;
		background: ${({ theme }) =>
			theme.mode === 'dark'
				? 'rgba(219, 177, 84, 0.12)'
				: 'rgba(168, 124, 31, 0.1)'};
	}

	.in .notifs .ncap {
		display: inline-block;
		font-family: var(--fh);
		font-weight: 600;
		font-size: 17px;
		letter-spacing: 0;
		text-transform: lowercase;
		/* gold only sits on dark surfaces; flips to gold-ink on cream */
		color: ${({ theme }) =>
			theme.mode === 'dark' ? 'var(--gold)' : 'var(--gold-ink)'};
		transform: rotate(-1.2deg);
		padding-bottom: 1px;
	}

	.in .notifs .nr {
		position: relative;
		display: block;
		width: 100%;
		text-align: left;
		background: none;
		border: 0;
		padding: 9px 0 9px 13px;
	}

	/* little gold edge on the row's left side */
	.in .notifs .nr::before {
		content: '';
		position: absolute;
		left: 0;
		top: 13px;
		bottom: 13px;
		width: 3px;
		border-radius: 99px;
		background: ${({ theme }) =>
			theme.mode === 'dark' ? 'var(--gold)' : 'var(--gold-ink)'};
	}

	.in .notifs button.nr {
		cursor: pointer;
	}

	.in .notifs button.nr:active b {
		color: var(--teal-dark);
	}

	.in .notifs .nr + .nr {
		border-top: 1px dashed rgba(var(--ink-rgb), 0.12);
	}

	.in .notifs b {
		font-family: var(--fb);
		font-size: 15px;
		font-weight: 700;
		letter-spacing: 0;
		text-transform: none;
		color: var(--ink);
		line-height: 1.35;
		display: block;
	}

	.in .notifs u {
		text-decoration: none;
		display: block;
		font-family: var(--fb);
		font-size: 13px;
		font-weight: 500;
		color: var(--mut);
		margin-top: 2px;
		line-height: 1.45;
	}

	/* bottom utility strip: contact mail + (logged-in) settings and logout */
	.foot {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 2px;
		padding: 8px 6px 2px;
		border-top: 1px dashed rgba(var(--ink-rgb), 0.12);
	}

	/* the mailto anchor must not inherit the sheet's full-width row look */
	.in .foot a {
		width: 40px;
		padding: 0;
		justify-content: center;
		border-radius: 50%;
	}

	.in .foot a:hover {
		background: none;
	}

	/* M10-D: logged-out top — prijava/registracija ghost pill + a quiet
	   Google pill in the same grammar (only the G mark identifies it) */
	.authpills {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding: 6px 6px 12px;
	}

	.ghostpill,
	.gpill {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
		width: 100%;
		height: 46px;
		padding: 0 22px;
		border-radius: var(--r-pill);
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		background: none;
		color: var(--brown);
		cursor: pointer;
		transition:
			border-color 0.2s,
			color 0.2s;
	}

	.ghostpill {
		font-family: var(--fd);
		font-size: 14px;
		letter-spacing: 0.4px;
		text-transform: uppercase;
	}

	.gpill {
		font-family: var(--fb);
		font-size: 14.5px;
		font-weight: 600;
		line-height: 20px;
		/* clicks land on the invisible GIS button stretched above */
		pointer-events: none;
	}

	.ghostpill:hover {
		border-color: var(--ink);
		color: var(--ink);
	}

	.gwrap {
		position: relative;
	}

	.gwrap:hover .gpill {
		border-color: var(--ink);
		color: var(--ink);
	}

	.gwrap .gis {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.001;
		transform-origin: 0 0;
	}
`;

// ---------------------------- footer ---------------------------- //

/* Variant 02 footer (GO 2026-09-01): compact /info hub — 4 numbered hairline
   rows, legal links demoted to a dot-separated line, brand strip below.
   Sits on the page background; homepage only (see GlobalFooter). */
export const FooterBlock = styled.footer`
	text-align: left;
	padding-bottom: 26px;

	${SHELL_BREAK} {
		padding-bottom: 96px;
	}

	.rows {
		border-top: 1px solid var(--hair);
	}

	.row {
		display: flex;
		align-items: center;
		gap: clamp(12px, 2.4vw, 26px);
		padding: 17px var(--pad);
		border-bottom: 1px solid var(--hair);
		text-decoration: none;
		cursor: pointer;
		transition: background 0.25s;
	}

	.row:hover {
		background: rgba(var(--ink-rgb), 0.035);
	}

	.num {
		font-family: var(--fd);
		font-size: 12px;
		color: var(--mut);
		letter-spacing: 1px;
		width: 26px;
		flex-shrink: 0;
	}

	.row h3 {
		font-size: clamp(17px, 1.8vw, 22px);
		flex: 1;
	}

	.desc {
		font-size: 13.5px;
		color: var(--mut);
	}

	.arr {
		width: 34px;
		height: 34px;
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: 0.3s;
		flex-shrink: 0;
		color: var(--ink);
	}

	.row:hover .arr {
		background: var(--teal);
		border-color: var(--teal);
		color: var(--teal-deep);
		transform: rotate(-45deg);
	}

	.legal {
		padding: 16px var(--pad) 0;
		font-size: 12.5px;
		line-height: 2;
		color: var(--mut);
	}

	.legal a {
		color: var(--mut);
		text-decoration: none;
		transition: color 0.2s;
	}

	.legal a:hover {
		color: var(--teal-dark);
	}

	.legal i {
		font-style: normal;
		color: rgba(var(--ink-rgb), 0.25);
		padding: 0 7px;
	}

	.strip {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 18px;
		flex-wrap: wrap;
		padding: 14px var(--pad) 0;
	}

	.brand {
		display: flex;
		align-items: end;
		gap: 14px;
		color: var(--logo);
	}

	.copy {
		color: var(--brown);
		font-size: 13px;
	}

	.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--logo);
		font-size: 24px;
		transform: rotate(-1.6deg);
	}

	.socials {
		display: flex;
		gap: 18px;
		align-items: center;
	}

	/* year rides with the socials on mobile only; desktop keeps it by the logo */
	.socials .copy {
		display: none;
	}

	.socials a {
		color: var(--brown);
		display: inline-flex;
		align-items: center;
		transition: color 0.2s;
	}

	.socials a:hover {
		color: var(--teal-dark);
	}

	/* mobile: brand + hand move ABOVE the rows, year rides inline with the
	   socials. The strip dissolves (display: contents) so its children become
	   footer flex items and can be reordered around .rows/.legal. */
	@media (max-width: 720px) {
		display: flex;
		flex-direction: column;
		padding-top: 30px;

		.row {
			padding: 13px var(--pad);
		}

		.row h3 {
			font-size: 15px;
		}

		.desc {
			display: none;
		}

		.arr {
			width: 28px;
			height: 28px;
		}

		.strip {
			display: contents;
		}

		.brand {
			order: 1;
			align-self: center;
		}

		.brand .copy {
			display: none;
		}

		.hand {
			order: 2;
			align-self: center;
			font-size: 26px;
			margin: 8px 0 24px;
		}

		.rows {
			order: 3;
		}

		.legal {
			order: 4;
			text-align: center;
			font-size: 11.5px;
		}

		.socials {
			order: 5;
			align-self: center;
			padding-top: 14px;
		}

		.socials .copy {
			display: inline;
		}
	}
`;

// ---------------------------- film grain ---------------------------- //

const grainShift = keyframes`
	0% { transform: translate(0, 0); }
	25% { transform: translate(-28px, 18px); }
	50% { transform: translate(18px, -22px); }
	75% { transform: translate(-12px, -8px); }
	100% { transform: translate(0, 0); }
`;

export const GrainOverlay = styled.div`
	position: fixed;
	inset: -100px;
	z-index: 9000;
	pointer-events: none;
	opacity: ${({ theme }) => (theme.mode === 'dark' ? 0.06 : 0.05)};
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.7'/%3E%3C/svg%3E");
	animation: ${grainShift} 1.2s steps(4) infinite;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;
